import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@ludus/ui'
import { EmptyState } from './SourceDetailTab.tsx'

/**
 * The source's own page for this record, framed verbatim.
 *
 * Deliberately not a re-implementation. Where a source already renders the item
 * the way its own users see it — a 3D preview, a rendered document, a live
 * quote — rebuilding that here means porting its presentation code and keeping
 * two versions in step forever. The frame keeps exactly one implementation, on
 * the side that owns it.
 *
 * The URL is the adapter's whole answer (`LeadDetail.embedUrl`): the console
 * neither composes it nor appends to it, because how that page admits a viewer
 * is the source's business. It is **not** an authenticated hop — no bearer, no
 * connection credential — so what the frame shows is whatever that origin shows
 * the viewer's own browser session, which for a signed-out viewer may be a
 * not-found wall. That is the correct outcome: passing our stored credential
 * through would hand every console viewer the read access the media proxy exists
 * to keep server-side.
 *
 * Not sandboxed, because a source's own app generally needs scripts, workers
 * and canvas — a sandbox strong enough to matter would break the page it is
 * framing. `referrerPolicy="no-referrer"` keeps the console's URLs out of the
 * source's logs, and the frame mounts only when the tab is opened, so a heavy
 * page never loads for records nobody previews.
 */

interface SourceEmbedTabProps {
  url: string
}

export function SourceEmbedTab({ url }: SourceEmbedTabProps) {
  const { t } = useTranslation()
  const [loaded, setLoaded] = useState(false)

  if (!isFramable(url)) {
    return (
      <EmptyState
        tone="error"
        text={t('pages.leads.detail.preview.unsafe', {
          defaultValue: 'The source offered a preview address that cannot be opened safely.',
        })}
      />
    )
  }

  return (
    <div className="relative h-[70vh] min-h-[480px] overflow-hidden rounded-lg border border-border/60 bg-background-secondary">
      {!loaded && (
        <div className="absolute inset-0 flex items-center justify-center text-xs text-muted">
          {t('pages.leads.detail.preview.loading', { defaultValue: 'Loading preview…' })}
        </div>
      )}
      <iframe
        src={url}
        title={t('pages.leads.detail.preview.title', { defaultValue: 'Source preview' })}
        className={cn(
          'h-full w-full border-0 transition-opacity',
          loaded ? 'opacity-100' : 'opacity-0',
        )}
        referrerPolicy="no-referrer"
        onLoad={() => setLoaded(true)}
      />
    </div>
  )
}

/**
 * The adapter is trusted, but this string reaches an `iframe src` — so a
 * `javascript:` URL would execute in the console's own origin. Checking the
 * scheme costs nothing and closes the one way this contract could be turned
 * into script execution.
 */
function isFramable(url: string): boolean {
  try {
    const { protocol } = new URL(url)
    return protocol === 'https:' || protocol === 'http:'
  } catch {
    return false
  }
}
